// Shared menu-day helpers: before the configured order_end (Mexico time)
// the menu is today's; after it, tomorrow's. Si ese día no tiene platillos
// (fin de semana, festivo), loadMenuDay avanza al siguiente que sí tenga.

import {
  DEFAULT_BUSINESS_HOURS,
  mexicoNow,
  mexicoNowMinutes,
  mexicoTodayString,
  toMinutes,
} from './businessHours';
import { API_URL } from './api';

// Cuántos días adelante se busca menú antes de rendirse (una semana cubre
// cualquier cierre razonable: fin de semana largo, vacaciones cortas).
const MAX_LOOKAHEAD_DAYS = 7;

const toDateString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** 'YYYY-MM-DD' -> Date local (evita el corrimiento UTC de new Date(str)). */
const parseDateString = (dateStr) => {
  const [year, month, day] = String(dateStr).split('-').map(Number);
  return new Date(year, month - 1, day);
};

/** Días entre dos 'YYYY-MM-DD' (b - a). */
export const daysBetween = (from, to) =>
  Math.round((parseDateString(to) - parseDateString(from)) / 86400000);

const addDays = (dateStr, days) => {
  const date = parseDateString(dateStr);
  return toDateString(new Date(date.getFullYear(), date.getMonth(), date.getDate() + days));
};

export const getMenuDate = (businessHours = DEFAULT_BUSINESS_HOURS) => {
  const now = mexicoNow();
  if (mexicoNowMinutes() >= toMinutes(businessHours.orderEnd)) {
    const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    return toDateString(tomorrow);
  }
  return toDateString(now);
};

const fetchItemsForDay = async (dateStr) => {
  const res = await fetch(`${API_URL}/items/get-items-day?day=${dateStr}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = await res.json();
  if (json.status !== 'success') throw new Error('Unexpected items response');
  return json.data || [];
};

const findNextMenuDay = async (businessHours) => {
  const start = getMenuDate(businessHours);
  for (let offset = 0; offset < MAX_LOOKAHEAD_DAYS; offset += 1) {
    const date = addDays(start, offset);
    const items = await fetchItemsForDay(date);
    if (items.length > 0) return { date, items };
  }
  // Ningún día de la semana tiene platillos: se devuelve el día base vacío
  // para que la UI muestre su estado de "sin menú" en vez de quedarse cargando.
  return { date: start, items: [] };
};

let cachedMenuDay = null;

/**
 * Día del menú vigente + sus platillos: { date: 'YYYY-MM-DD', items: [] }.
 * Cacheado por sesión (los tres consumidores —menú, combos, wizard— comparten
 * la misma respuesta en vez de pedir lo mismo tres veces).
 */
export const loadMenuDay = (businessHours) => {
  if (!cachedMenuDay) {
    cachedMenuDay = findNextMenuDay(businessHours).catch((error) => {
      cachedMenuDay = null; // no cachear el fallo: el siguiente intento reintenta
      throw error;
    });
  }
  return cachedMenuDay;
};

/** Invalida el caché (tras un pedido, para refrescar existencias). */
export const resetMenuDay = () => {
  cachedMenuDay = null;
};

/**
 * ¿Se puede pedir del menú que se está mostrando?
 * Cerrado (modo vitrina) cuando el día del menú está a 2+ días —el negocio no
 * toma pedidos con tanta anticipación— o cuando es hoy pero aún no llega la
 * hora de apertura (ordering_opens). Un menú de mañana sí acepta pedidos: es
 * el flujo normal de después del corte del día.
 */
export const getOrderingWindow = (menuDate, businessHours = DEFAULT_BUSINESS_HOURS) => {
  const opensAt = businessHours.orderingOpens || DEFAULT_BUSINESS_HOURS.orderingOpens;
  const daysAhead = daysBetween(mexicoTodayString(), menuDate);

  if (daysAhead >= 2) return { open: false, opensAt, daysAhead };
  if (daysAhead <= 0 && mexicoNowMinutes() < toMinutes(opensAt)) {
    return { open: false, opensAt, daysAhead };
  }
  return { open: true, opensAt, daysAhead };
};

/** 'YYYY-MM-DD' -> 'Lunes 27 de julio' (para decirle al cliente de qué día es el menú). */
export const formatMenuDayLabel = (dateStr) => {
  const label = parseDateString(dateStr)
    .toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' })
    .replace(',', ''); // es-MX intercala coma ("lunes, 20 de julio"); estorba en la frase
  return label.charAt(0).toUpperCase() + label.slice(1);
};

/** Convert a Google Drive share link to an embeddable thumbnail URL. */
export const formatDriveImage = (url) => {
  if (!url) return '';
  let fileId = '';
  const fileIdMatch = url.match(/\/d\/(.*?)(\/|$|\?)/);
  if (fileIdMatch) fileId = fileIdMatch[1];
  const ucMatch = url.match(/id=(.*?)($|&)/);
  if (ucMatch) fileId = ucMatch[1];
  if (!fileId) return url;
  return `https://drive.google.com/thumbnail?id=${fileId}&sz=w1000`;
};
